import { ReactNode } from 'react';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { MenuButtonProps } from '@nimbus-ds/menubutton';
import { PromotionType } from '@tiendanube/common/src/domains/marketing/promotions/PromotionTypes';
import { DiscountCircleIcon } from '@tiendanube/icons';
import { useNavegate } from 'App/hooks';
import {
  ActionProp,
  ActionsDesktop,
  ErrorScreen,
  HeaderContent,
  HeaderTop,
  IonPageStratus,
  PopoverMenu,
} from 'commons/components';
import { MoreDiscounts } from 'domains/Marketing/components/MoreDiscounts';
import {
  useMoreDiscountsModal,
  useTranslationMarketing,
} from 'domains/Marketing/hook';
import { NewPromoAlert } from 'domains/Marketing/Promotions/components/NewPromoAlert';
import { FreeShippingHelpLink } from './FreeShippingHelpLink';
import { FreeShippingListEmpty } from './FreeShippingListEmpty';
import { buildNewPromotionUrl } from '../../Promotions/utils';
import {
  freeShippingCloseAdoptionAlert,
  freeShippingOpenAdoptionAlert,
  freeShippingShowAdoptionAlert,
} from '../tracking';
import { buildNewFreeShippingUrl } from '../utils';

interface FreeShippingPageProps {
  skeleton: ReactNode;
  isLoading: boolean;
  isEmpty: boolean;
  isError: boolean;
  children: ReactNode;
  onRetry: () => void;
}

export function FreeShippingListPage({
  skeleton,
  isLoading,
  isEmpty,
  isError,
  children,
  onRetry,
}: Readonly<FreeShippingPageProps>) {
  const t = useTranslationMarketing('freeShipping');
  const tMoreDiscounts = useTranslationMarketing('moreDiscounts');
  const { goTo } = useNavegate();

  const { moreDiscountsIsOpen, openMoreDiscounts, closeMoreDiscounts } =
    useMoreDiscountsModal('freeShipping');

  const title = isEmpty ? t('titleEmpty') : t('title');
  const gotoCreate = () => goTo(buildNewFreeShippingUrl());
  const gotoCreatePromotion = (promotionType: PromotionType) => {
    freeShippingOpenAdoptionAlert(promotionType);
    goTo(buildNewPromotionUrl({ promotionType }));
  };

  const createFreeShippingAction: ActionProp = {
    icon: PlusCircleIcon,
    children: t('add'),
    appearance: 'default',
    onClick: gotoCreate,
  };
  const mobileMenuActions: MenuButtonProps[] = [
    {
      startIcon: DiscountCircleIcon,
      label: tMoreDiscounts('ctas.open'),
      onClick: openMoreDiscounts,
    },
  ];
  const desktopActions: ActionProp[] = [
    {
      icon: DiscountCircleIcon,
      children: tMoreDiscounts('ctas.open'),
      appearance: 'default',
      onClick: openMoreDiscounts,
    },
    {
      icon: PlusCircleIcon,
      children: t('addFreeShipping'),
      appearance: 'primary',
      onClick: gotoCreate,
    },
  ];

  return (
    <IonPageStratus
      width="medium"
      headerTop={
        <HeaderTop
          mainAction={!isEmpty ? createFreeShippingAction : undefined}
          actions={
            <PopoverMenu
              buttonColor="transparent"
              actions={mobileMenuActions}
            />
          }
        />
      }
      headerContent={
        !isEmpty ? (
          <HeaderContent
            title={title}
            actions={<ActionsDesktop actions={desktopActions} />}
          />
        ) : undefined
      }
      headerFixed
    >
      {isLoading && skeleton}
      {isError && <ErrorScreen onRetry={onRetry} />}
      {isEmpty && <FreeShippingListEmpty onCreate={gotoCreate} />}
      {!isLoading && !isError && !isEmpty && (
        <>
          <NewPromoAlert
            onClick={gotoCreatePromotion}
            onRemove={freeShippingCloseAdoptionAlert}
            onShow={freeShippingShowAdoptionAlert}
          />
          {children}
        </>
      )}
      {!isError && <FreeShippingHelpLink />}

      <MoreDiscounts
        isOpen={moreDiscountsIsOpen}
        onClose={closeMoreDiscounts}
      />
    </IonPageStratus>
  );
}
